import React, { useState, useEffect } from 'react';
import { Schedule } from '../types';
import { Modal, Input, Select, Button } from './ui';

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (scheduleData: Partial<Schedule>) => Promise<void>;
  editingSchedule: Schedule | null;
  selectedRepo: string;
  subscriptionTier?: 'FREE' | 'PRO';
}

export default function ScheduleModal({
  isOpen,
  onClose,
  onSave,
  editingSchedule,
  selectedRepo,
  subscriptionTier = 'FREE',
}: ScheduleModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    scheduleType: 'DAILY' as 'DAILY' | 'WEEKLY' | 'ONCE',
    startTime: '22:00',
    endTime: '06:00',
    startDate: '',
    endDate: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    daysOfWeek: [] as string[],
    enabled: true,
    branchName: '',
  });

  const [branches, setBranches] = useState<Array<{ name: string; frozen: boolean }>>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    if (editingSchedule) {
      setFormData({
        name: editingSchedule.name,
        scheduleType: editingSchedule.scheduleType,
        startTime: editingSchedule.startTime,
        endTime: editingSchedule.endTime,
        startDate: editingSchedule.startDate
          ? new Date(editingSchedule.startDate).toISOString().split('T')[0]
          : '',
        endDate: editingSchedule.endDate
          ? new Date(editingSchedule.endDate).toISOString().split('T')[0]
          : '',
        timezone: editingSchedule.timezone,
        daysOfWeek: editingSchedule.daysOfWeek ? editingSchedule.daysOfWeek.split(',') : [],
        enabled: editingSchedule.enabled,
        branchName: editingSchedule.branchName || '',
      });
    } else {
      setFormData({
        name: '',
        scheduleType: 'DAILY',
        startTime: '22:00',
        endTime: '06:00',
        startDate: '',
        endDate: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        daysOfWeek: [],
        enabled: true,
        branchName: '',
      });
    }
  }, [editingSchedule, isOpen]);

  useEffect(() => {
    if (isOpen && selectedRepo) {
      const [owner, repo] = selectedRepo.split('/');
      loadBranches(owner, repo);
    }
  }, [isOpen, selectedRepo]);

  const loadBranches = async (owner: string, repo: string) => {
    setLoadingBranches(true);
    try {
      const response = await fetch(`/api/v1/repos/${owner}/${repo}/branches`);
      if (!response.ok) {
        throw new Error('Failed to fetch branches');
      }
      const data = await response.json();
      const branchesList = data.branches || [];
      setBranches(branchesList);

      // Set default branch to "main" if it exists, otherwise use first branch
      if (!editingSchedule && branchesList.length > 0) {
        const mainBranch = branchesList.find((b: { name: string }) => b.name === 'main');
        const defaultBranch = mainBranch ? 'main' : branchesList[0].name;
        setFormData(prev => ({ ...prev, branchName: defaultBranch }));
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleDayToggle = (dayIndex: number) => {
    setFormData(prev => {
      const currentDays = prev.daysOfWeek.map(Number);
      const day = String(dayIndex);

      // If already selected, remove it
      if (prev.daysOfWeek.includes(day)) {
        // If only one day selected, just remove it
        if (currentDays.length === 1) {
          return { ...prev, daysOfWeek: [] };
        }

        // If all 7 days selected, allow removing any day
        if (currentDays.length === 7) {
          return { ...prev, daysOfWeek: prev.daysOfWeek.filter(d => d !== day) };
        }

        // Determine the actual range bounds considering week wrapping
        const hasWeekWrap = currentDays.includes(6) && currentDays.includes(0);
        let rangeStart, rangeEnd;

        if (hasWeekWrap) {
          // Find the gap - the range wraps around
          const sorted = [...currentDays].sort((a, b) => a - b);
          // Find where the gap is
          let gapIndex = -1;
          for (let i = 0; i < sorted.length - 1; i++) {
            if (sorted[i + 1] - sorted[i] > 1) {
              gapIndex = i;
              break;
            }
          }
          rangeStart = sorted[gapIndex + 1]; // First day after gap
          rangeEnd = sorted[gapIndex]; // Last day before gap
        } else {
          const sorted = [...currentDays].sort((a, b) => a - b);
          rangeStart = sorted[0];
          rangeEnd = sorted[sorted.length - 1];
        }

        // Only allow removing first or last day to maintain sequence
        if (dayIndex === rangeStart || dayIndex === rangeEnd) {
          return { ...prev, daysOfWeek: prev.daysOfWeek.filter(d => d !== day) };
        }
        return prev; // Can't remove middle days
      }

      // If no days selected, allow any day
      if (currentDays.length === 0) {
        return { ...prev, daysOfWeek: [day] };
      }

      // Determine adjacency considering week wrapping
      const hasWeekWrap = currentDays.includes(6) && currentDays.includes(0);
      let rangeStart, rangeEnd;

      if (hasWeekWrap) {
        // Find the gap in a wrapped week
        const sorted = [...currentDays].sort((a, b) => a - b);
        let gapIndex = -1;
        for (let i = 0; i < sorted.length - 1; i++) {
          if (sorted[i + 1] - sorted[i] > 1) {
            gapIndex = i;
            break;
          }
        }
        rangeStart = sorted[gapIndex + 1];
        rangeEnd = sorted[gapIndex];
      } else {
        const sorted = [...currentDays].sort((a, b) => a - b);
        rangeStart = sorted[0];
        rangeEnd = sorted[sorted.length - 1];
      }

      const isAdjacentToStart = dayIndex === (rangeStart - 1 + 7) % 7;
      const isAdjacentToEnd = dayIndex === (rangeEnd + 1) % 7;

      if (isAdjacentToStart || isAdjacentToEnd) {
        return { ...prev, daysOfWeek: [...prev.daysOfWeek, day] };
      }

      return prev; // Not adjacent, don't add
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert('Please enter a schedule name');
      return;
    }

    if (formData.scheduleType === 'ONCE') {
      if (!formData.startDate || !formData.endDate) {
        alert('Please select both start and end dates for one-time schedule');
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      if (startDate < today) {
        alert('Start date cannot be in the past');
        return;
      }

      if (endDate < today) {
        alert('End date cannot be in the past');
        return;
      }

      if (startDate > endDate) {
        alert('End date must be on or after start date');
        return;
      }
    }

    const [owner, repo] = selectedRepo.split('/');

    const scheduleData: any = {
      ...formData,
      name: formData.name.trim(),
      owner,
      repo,
      daysOfWeek: formData.scheduleType === 'WEEKLY' ? formData.daysOfWeek.map(Number) : undefined,
      startDate:
        formData.scheduleType === 'ONCE' && formData.startDate
          ? new Date(formData.startDate).toISOString()
          : undefined,
      endDate:
        formData.scheduleType === 'ONCE' && formData.endDate
          ? new Date(formData.endDate).toISOString()
          : undefined,
      branchName: formData.branchName || undefined,
    };

    await onSave(scheduleData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>
        <h3>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</h3>
      </Modal.Header>
      <Modal.Body>
        <Input
          id="scheduleName"
          label="Schedule Name"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Nightly Freeze"
          required
        />

        <Select
          id="branchName"
          label="Branch"
          value={formData.branchName}
          onChange={e => setFormData({ ...formData, branchName: e.target.value })}
        >
          {loadingBranches ? (
            <option disabled>Loading branches...</option>
          ) : (
            branches.map(branch => (
              <option key={branch.name} value={branch.name}>
                {branch.name} {branch.frozen ? '(Frozen)' : ''}
              </option>
            ))
          )}
        </Select>

        <Select
          id="scheduleType"
          label="Schedule Type"
          value={formData.scheduleType}
          onChange={e => setFormData({ ...formData, scheduleType: e.target.value as any })}
          disabled={!!editingSchedule}
        >
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="ONCE" disabled={subscriptionTier === 'FREE'}>
            Custom (One-time){subscriptionTier === 'FREE' ? ' (Pro only)' : ''}
          </option>
        </Select>

        {formData.scheduleType === 'ONCE' && (
          <div className="time-inputs">
            <div className="form-group">
              <label htmlFor="startDate">
                <i className="fa-solid fa-calendar-days" style={{ color: '#6B3F8C' }}></i> Start
                Date
                <small style={{ display: 'block', fontWeight: 'normal', opacity: 0.7 }}>
                  Date to begin freeze
                </small>
              </label>
              <input
                type="date"
                id="startDate"
                className="form-control"
                value={formData.startDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="endDate">
                <i className="fa-solid fa-calendar-days" style={{ color: '#6B3F8C' }}></i> End Date
                <small style={{ display: 'block', fontWeight: 'normal', opacity: 0.7 }}>
                  Date to end freeze
                </small>
              </label>
              <input
                type="date"
                id="endDate"
                className="form-control"
                value={formData.endDate}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                required
              />
            </div>
          </div>
        )}

        <div className="time-inputs">
          <div className="form-group">
            <label htmlFor="startTime">
              <i className="fa-solid fa-snowflake" style={{ color: '#60a5fa' }}></i> Freeze Time
              <small style={{ display: 'block', fontWeight: 'normal', opacity: 0.7 }}>
                When to freeze the repository
              </small>
            </label>
            <input
              type="time"
              id="startTime"
              className="form-control"
              value={formData.startTime}
              onChange={e => setFormData({ ...formData, startTime: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="endTime">
              <i className="fa-solid fa-fire" style={{ color: '#f97316' }}></i> Unfreeze Time
              <small style={{ display: 'block', fontWeight: 'normal', opacity: 0.7 }}>
                When to unfreeze the repository
              </small>
            </label>
            <input
              type="time"
              id="endTime"
              className="form-control"
              value={formData.endTime}
              onChange={e => setFormData({ ...formData, endTime: e.target.value })}
              required
            />
          </div>
        </div>

        {formData.scheduleType === 'WEEKLY' && (
          <div className="form-group">
            <label>
              Active Days
              <small style={{ display: 'block', fontWeight: 'normal', opacity: 0.7 }}>
                Select consecutive days (e.g., Fri-Mon for weekend freeze)
              </small>
            </label>
            <div className="days-selector">
              {[
                { name: 'Mon', index: 1 },
                { name: 'Tue', index: 2 },
                { name: 'Wed', index: 3 },
                { name: 'Thu', index: 4 },
                { name: 'Fri', index: 5 },
                { name: 'Sat', index: 6 },
                { name: 'Sun', index: 0 },
              ].map(({ name: day, index }) => {
                const currentDays = formData.daysOfWeek.map(Number);
                const isSelected = formData.daysOfWeek.includes(String(index));

                let isClickable = currentDays.length === 0 || isSelected;

                if (!isClickable && currentDays.length > 0) {
                  const hasWeekWrap = currentDays.includes(6) && currentDays.includes(0);
                  let rangeStart, rangeEnd;

                  if (hasWeekWrap) {
                    const sorted = [...currentDays].sort((a, b) => a - b);
                    let gapIndex = -1;
                    for (let i = 0; i < sorted.length - 1; i++) {
                      if (sorted[i + 1] - sorted[i] > 1) {
                        gapIndex = i;
                        break;
                      }
                    }
                    rangeStart = sorted[gapIndex + 1];
                    rangeEnd = sorted[gapIndex];
                  } else {
                    const sorted = [...currentDays].sort((a, b) => a - b);
                    rangeStart = sorted[0];
                    rangeEnd = sorted[sorted.length - 1];
                  }

                  const isAdjacentToStart = index === (rangeStart - 1 + 7) % 7;
                  const isAdjacentToEnd = index === (rangeEnd + 1) % 7;
                  isClickable = isAdjacentToStart || isAdjacentToEnd;
                }

                return (
                  <div key={day} className="day-checkbox">
                    <input
                      type="checkbox"
                      id={`day${index}`}
                      checked={isSelected}
                      onChange={() => handleDayToggle(index)}
                      disabled={!isClickable}
                    />
                    <label htmlFor={`day${index}`}>{day}</label>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Select
          id="scheduleTimezone"
          label="Timezone"
          value={formData.timezone}
          onChange={e => setFormData({ ...formData, timezone: e.target.value })}
        >
          <option value="UTC">UTC</option>
          <option value="America/New_York">America/New_York</option>
          <option value="America/Chicago">America/Chicago</option>
          <option value="America/Denver">America/Denver</option>
          <option value="America/Los_Angeles">America/Los_Angeles</option>
          <option value="Europe/London">Europe/London</option>
          <option value="Europe/Paris">Europe/Paris</option>
          <option value="Europe/Berlin">Europe/Berlin</option>
          <option value="Asia/Tokyo">Asia/Tokyo</option>
          <option value="Asia/Shanghai">Asia/Shanghai</option>
          <option value="Australia/Sydney">Australia/Sydney</option>
        </Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Save Schedule
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
